import { WebappResponse } from '@eventbrite/event-listing-service-client';
import { IncomingHttpHeaders, ServerResponse } from 'http';
import { getUserIdFromHeaders } from '@/utils/ebRequestHeaders';
import { isNonEmptyString } from '@/utils/apiRouteUtils';

type EventStatus = NonNullable<NonNullable<WebappResponse['basicInfo']>['status']> | undefined;

const UPSTREAM_SOURCE_HEADER = 'x-upstream-source';
/**
 * Edge cache: 300s (5m)
 * Stale while revalidate: 43200s (12h)
 * Stale if error: 86400s (24h)
 */
const CACHE_CONTROL_LIVE_TTL = 's-maxage=300, stale-while-revalidate=43200, stale-if-error=86400';
/**
 * Edge cache: 3600s (1h)
 * Stale while revalidate: 86400s (24h)
 * Stale if error: 604800s (7d)
 */
const CACHE_CONTROL_PAST_TTL = 's-maxage=3600, stale-while-revalidate=86400, stale-if-error=604800';
// No cache
const CACHE_CONTROL_NO_CACHE = 'no-store, no-cache, must-revalidate, proxy-revalidate';
// Cache for 4xx errors
const CACHE_CONTROL_ERROR = 's-maxage=60';

const LIVE_EVENT_STATUS: EventStatus[] = ['live', 'started'];
const PAST_EVENT_STATUS: EventStatus[] = ['ended', 'completed', 'deleted', 'canceled'];

type CacheControlOptions = {
  forceNoCache?: boolean;
};

export const setCacheControl = (
  response: ServerResponse,
  context: WebappResponse,
  { forceNoCache = false }: CacheControlOptions = {},
) => {
  response.setHeader(UPSTREAM_SOURCE_HEADER, 'VALKYRIE');

  const status = context.basicInfo?.status;

  let cacheControl = undefined;
  if (LIVE_EVENT_STATUS.includes(status)) {
    cacheControl = CACHE_CONTROL_LIVE_TTL;
  } else if (PAST_EVENT_STATUS.includes(status)) {
    // Recurring (series) events can transition from completed back to live,
    // so use the shorter live TTL to avoid serving stale completed pages.
    cacheControl = context.basicInfo?.isSeries ? CACHE_CONTROL_LIVE_TTL : CACHE_CONTROL_PAST_TTL;
  }

  if (forceNoCache || !cacheControl) {
    cacheControl = CACHE_CONTROL_NO_CACHE;
  }

  response.setHeader('Cache-Control', cacheControl);
};

/**
 * Set cache control headers for error responses, with a short TTL for logged-out users and no caching for logged-in users.
 *
 * Note on local testing: Bear in mind that Next.js dev server overrides the Cache-Control header in all responses.
 * In order to see the cache control headers you will need to run a production build instead.
 */
// Eventbrite-internal traffic (CI runners, E2E gating suites) tags requests
// with this `x-eb-bot` value. We skip the 60s edge cache for these so e2e
// suites that publish-then-immediately-navigate to a fresh event URL don't
// get trapped behind a transient propagation-lag 404.
const INTERNAL_E2E_BOT_HEADER = 'Internal-EKS';

export const setCacheControlForErrorResponse = (
  response: ServerResponse,
  requestHeaders: IncomingHttpHeaders = response.req?.headers ?? {},
) => {
  const isLoggedIn = isNonEmptyString(getUserIdFromHeaders(requestHeaders));
  const isInternalE2eTraffic = requestHeaders['x-eb-bot'] === INTERNAL_E2E_BOT_HEADER;
  if (!isLoggedIn && !isInternalE2eTraffic) {
    response.setHeader('Cache-Control', CACHE_CONTROL_ERROR);
  } else {
    response.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  }
  response.setHeader(UPSTREAM_SOURCE_HEADER, 'VALKYRIE');
};
